import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Drawer, Field } from "@/components/ui-kit";
import { StatusBadge } from "@/pages/Dashboard";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight } from "lucide-react";

const STATUSES = [
  { value: "pending", label: "Pending" },
  { value: "assigned", label: "Assigned" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "canceled", label: "Canceled" },
];
const STATUS_VALUES = STATUSES.map((s) => s.value);

// Same palette as StatusBadge (Dashboard.jsx) so a status reads the same colour everywhere.
const STATUS_CHIP = {
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  assigned: "bg-sky-50 text-sky-800 border-sky-200",
  in_progress: "bg-indigo-50 text-indigo-800 border-indigo-200",
  completed: "bg-emerald-50 text-emerald-800 border-emerald-200",
  canceled: "bg-rose-50 text-rose-800 border-rose-200",
};
const STATUS_CHIP_FALLBACK = "bg-slate-50 text-slate-700 border-slate-200";

// Driver colour dots use their own palette so they never get confused with a status colour.
const DRIVER_DOT_COLORS = [
  "bg-purple-500", "bg-teal-500", "bg-orange-500", "bg-pink-500",
  "bg-lime-500", "bg-cyan-500", "bg-fuchsia-500", "bg-blue-500",
];
function driverColor(driverId) {
  if (!driverId) return "bg-slate-300";
  let hash = 0;
  for (let i = 0; i < driverId.length; i++) hash = (hash * 31 + driverId.charCodeAt(i)) >>> 0;
  return DRIVER_DOT_COLORS[hash % DRIVER_DOT_COLORS.length];
}

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEK_START_HOUR = 5;
const WEEK_END_HOUR = 21;
const ROW_H = 48; // px per hour in week view

// ----------- Local date helpers (all dates are plain "YYYY-MM-DD" strings, no timezone math) -----------
function pad2(n) { return String(n).padStart(2, "0"); }
function ymd(y, m, d) { return `${y}-${pad2(m)}-${pad2(d)}`; }
function todayYmd() {
  const t = new Date();
  return ymd(t.getFullYear(), t.getMonth() + 1, t.getDate());
}
function parseYmd(s) {
  const [y, m, d] = s.split("-").map(Number);
  return { y, m, d };
}
function toDateObj(s) {
  const { y, m, d } = parseYmd(s);
  return new Date(y, m - 1, d);
}
function fromDateObj(dt) {
  return ymd(dt.getFullYear(), dt.getMonth() + 1, dt.getDate());
}
function addDays(s, n) {
  const dt = toDateObj(s);
  dt.setDate(dt.getDate() + n);
  return fromDateObj(dt);
}
function addMonths(s, delta) {
  const { y, m, d } = parseYmd(s);
  const dt = new Date(y, m - 1 + delta, 1);
  const lastDay = new Date(dt.getFullYear(), dt.getMonth() + 1, 0).getDate();
  dt.setDate(Math.min(d, lastDay));
  return fromDateObj(dt);
}
function startOfWeek(s) {
  const dt = toDateObj(s);
  dt.setDate(dt.getDate() - dt.getDay());
  return fromDateObj(dt);
}
function startOfMonth(s) {
  const { y, m } = parseYmd(s);
  return ymd(y, m, 1);
}
function endOfMonth(s) {
  const { y, m } = parseYmd(s);
  return fromDateObj(new Date(y, m, 0));
}
function weekRange(s) {
  const from = startOfWeek(s);
  return { from, to: addDays(from, 6) };
}
function monthGridRange(s) {
  const from = startOfWeek(startOfMonth(s));
  const to = addDays(startOfWeek(endOfMonth(s)), 6);
  return { from, to };
}
function monthGridDays(s) {
  const { from, to } = monthGridRange(s);
  const curMonth = parseYmd(startOfMonth(s)).m;
  const days = [];
  let cursor = from;
  while (cursor <= to) {
    days.push({ date: cursor, inMonth: parseYmd(cursor).m === curMonth });
    cursor = addDays(cursor, 1);
  }
  return days;
}
function computeRange(view, dateStr) {
  if (view === "day") return { from: dateStr, to: dateStr };
  if (view === "week") return weekRange(dateStr);
  return monthGridRange(dateStr);
}
function dayLabel(s) {
  return toDateObj(s).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}
function monthLabel(s) {
  return toDateObj(s).toLocaleDateString(undefined, { month: "long", year: "numeric" });
}
function weekLabel(s) {
  const { from, to } = weekRange(s);
  const f = toDateObj(from).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const t = toDateObj(to).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return `${f} to ${t}`;
}
function dateLabel(view, s) {
  if (view === "day") return dayLabel(s);
  if (view === "week") return weekLabel(s);
  return monthLabel(s);
}
function weekdayShort(s) {
  return toDateObj(s).toLocaleDateString(undefined, { weekday: "short" });
}
function fmtHour(h) {
  const ap = h >= 12 ? "PM" : "AM";
  let hh = h % 12; if (hh === 0) hh = 12;
  return `${hh} ${ap}`;
}
function fmtTime(dt) {
  if (!dt) return "";
  const t = dt.split("T")[1] || "";
  const [hhStr, mm] = t.split(":");
  let hh = Number(hhStr) || 0;
  const ap = hh >= 12 ? "p" : "a";
  hh = hh % 12; if (hh === 0) hh = 12;
  return `${hh}:${mm || "00"}${ap}`;
}
function fmtFullDateTime(dt) {
  if (!dt) return "";
  const datePart = dt.split("T")[0];
  const label = toDateObj(datePart).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  return `${label} ${fmtTime(dt)}`;
}
function lastName(name) {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  return parts[parts.length - 1];
}
function initials(name) {
  if (!name) return "";
  return name.trim().split(/\s+/).map((p) => p[0]).join("").slice(0, 2).toUpperCase();
}
function eventDay(ev) {
  return (ev.pickup_datetime || "").slice(0, 10);
}

function EventChip({ ev, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`cal-event-${ev.id}`}
      title={`${fmtTime(ev.pickup_datetime)} ${ev.patient_name}`}
      className={`w-full text-left text-[10px] leading-tight px-1.5 py-1 rounded border truncate flex items-center gap-1 hover:opacity-80 ${STATUS_CHIP[ev.status] || STATUS_CHIP_FALLBACK}`}
    >
      <span className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${driverColor(ev.driver_id)}`} />
      <span className="font-mono shrink-0">{fmtTime(ev.pickup_datetime)}</span>
      <span className="truncate hidden sm:inline">{lastName(ev.patient_name)}</span>
      <span className="truncate sm:hidden">{initials(ev.patient_name)}</span>
    </button>
  );
}

export default function Calendar() {
  const { user } = useAuth();
  const isClient = user?.role === "client_user";
  const [params, setParams] = useSearchParams();

  const view = params.get("view") || "month";
  const dateStr = params.get("date") || todayYmd();
  const driverId = params.get("driver_id") || "";
  const companyId = params.get("company_id") || "";
  const customerId = params.get("customer_id") || "";
  const statusParam = params.get("status");
  const statuses = statusParam === null ? STATUS_VALUES : statusParam.split(",").filter(Boolean);

  const [drivers, setDrivers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerText, setCustomerText] = useState("");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const range = useMemo(() => computeRange(view, dateStr), [view, dateStr]);
  const viewIncludesToday = todayYmd() >= range.from && todayYmd() <= range.to;

  const updateParams = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "") next.delete(k);
      else next.set(k, v);
    });
    setParams(next, { replace: true });
  };

  const setStatuses = (arr) => {
    const next = new URLSearchParams(params);
    if (arr.length === STATUS_VALUES.length) next.delete("status");
    else next.set("status", arr.join(","));
    setParams(next, { replace: true });
  };
  const toggleStatus = (val) => {
    const set = new Set(statuses);
    if (set.has(val)) set.delete(val); else set.add(val);
    setStatuses(STATUS_VALUES.filter((s) => set.has(s)));
  };

  const clearFilters = () => {
    setCustomerText("");
    const next = new URLSearchParams(params);
    ["driver_id", "company_id", "customer_id", "status"].forEach((k) => next.delete(k));
    setParams(next, { replace: true });
  };

  const goPrev = () => {
    if (view === "day") updateParams({ date: addDays(dateStr, -1) });
    else if (view === "week") updateParams({ date: addDays(dateStr, -7) });
    else updateParams({ date: addMonths(dateStr, -1) });
  };
  const goNext = () => {
    if (view === "day") updateParams({ date: addDays(dateStr, 1) });
    else if (view === "week") updateParams({ date: addDays(dateStr, 7) });
    else updateParams({ date: addMonths(dateStr, 1) });
  };
  const goToday = () => updateParams({ date: todayYmd() });
  const goToDay = (d) => updateParams({ view: "day", date: d });

  // Reference lists for filter dropdowns. client_user cannot call /drivers or /companies
  // (403, same as Rides.jsx), so the company filter is hidden for that role and the driver
  // list is instead derived from whatever rides are currently in view.
  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    if (!isClient) {
      api.get("/drivers").then((r) => setDrivers(r.data)).catch(() => {});
      api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
    }
  }, [isClient]);

  // Keep the customer search box in sync with the URL (deep links, Clear filters, drawer shortcut).
  useEffect(() => {
    if (!customerId) { setCustomerText(""); return; }
    const cust = customers.find((c) => c.id === customerId);
    if (cust) setCustomerText(`${cust.first_name} ${cust.last_name}`);
  }, [customerId, customers]);

  // One request per visible range; refetches on navigation or filter change.
  useEffect(() => {
    if (statuses.length === 0) { setEvents([]); return undefined; }
    let cancelled = false;
    setLoading(true);
    const qs = new URLSearchParams();
    qs.set("from", range.from);
    qs.set("to", range.to);
    if (driverId) qs.set("driver_id", driverId);
    if (!isClient && companyId) qs.set("company_id", companyId);
    if (customerId) qs.set("customer_id", customerId);
    if (statuses.length < STATUS_VALUES.length) qs.set("status", statuses.join(","));
    api.get(`/rides/calendar?${qs.toString()}`)
      .then((r) => { if (!cancelled) setEvents(r.data.rides || []); })
      .catch((e) => {
        if (cancelled) return;
        setEvents([]);
        const st = e.response?.status;
        if (st !== 402 && st !== 403) toast.error(formatApiError(e.response?.data?.detail));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to, driverId, companyId, customerId, statuses.join(","), isClient]);

  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      const day = eventDay(e);
      if (!map[day]) map[day] = [];
      map[day].push(e);
    });
    return map;
  }, [events]);

  const monthDays = useMemo(() => monthGridDays(dateStr), [dateStr]);
  const weekDays = useMemo(() => {
    const { from } = weekRange(dateStr);
    return Array.from({ length: 7 }, (_, i) => addDays(from, i));
  }, [dateStr]);
  const weekHours = useMemo(
    () => Array.from({ length: WEEK_END_HOUR - WEEK_START_HOUR + 1 }, (_, i) => WEEK_START_HOUR + i),
    []
  );
  const dayGroups = useMemo(() => {
    const list = eventsByDate[dateStr] || [];
    const byHour = {};
    list.forEach((e) => {
      const hh = Number((e.pickup_datetime.split("T")[1] || "00:00").split(":")[0]) || 0;
      if (!byHour[hh]) byHour[hh] = [];
      byHour[hh].push(e);
    });
    return Object.keys(byHour).map(Number).sort((a, b) => a - b).map((hour) => ({ hour, items: byHour[hour] }));
  }, [eventsByDate, dateStr]);

  const summary = useMemo(() => {
    const counts = { pending: 0, assigned: 0, in_progress: 0, completed: 0, canceled: 0 };
    events.forEach((e) => { if (counts[e.status] != null) counts[e.status] += 1; });
    return { total: events.length, ...counts };
  }, [events]);

  const driverList = useMemo(() => {
    if (!isClient) return drivers.map((d) => ({ id: d.id, name: d.name }));
    const map = new Map();
    events.forEach((e) => { if (e.driver_id && !map.has(e.driver_id)) map.set(e.driver_id, e.driver_name || "Driver"); });
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [isClient, drivers, events]);

  const onCustomerTextChange = (e) => {
    const val = e.target.value;
    setCustomerText(val);
    if (val.trim() === "") { updateParams({ customer_id: null }); return; }
    const match = customers.find((c) => `${c.first_name} ${c.last_name}`.toLowerCase() === val.trim().toLowerCase());
    if (match) updateParams({ customer_id: match.id });
  };

  const filterByDriver = (id) => { updateParams({ driver_id: id }); setSelected(null); };
  const filterByCustomer = (id, fallbackName) => {
    const cust = customers.find((c) => c.id === id);
    setCustomerText(cust ? `${cust.first_name} ${cust.last_name}` : (fallbackName || ""));
    updateParams({ customer_id: id });
    setSelected(null);
  };

  const eventPosition = (ev) => {
    const t = (ev.pickup_datetime.split("T")[1] || "00:00");
    const [hhStr, mmStr] = t.split(":");
    const hh = Number(hhStr) || 0;
    const mm = Number(mmStr) || 0;
    let offsetHours = hh + mm / 60 - WEEK_START_HOUR;
    offsetHours = Math.max(0, Math.min(offsetHours, WEEK_END_HOUR - WEEK_START_HOUR));
    return offsetHours * ROW_H;
  };

  return (
    <div data-testid="calendar-page">
      <PageHeader subtitle="Operations" title="Calendar" />

      <div className="flex flex-wrap items-end gap-4 mb-4">
        <div className="inline-flex rounded-md border border-slate-200 overflow-hidden self-end">
          {["day", "week", "month"].map((v) => (
            <button
              key={v}
              type="button"
              data-testid={`cal-view-${v}`}
              onClick={() => updateParams({ view: v })}
              className={`px-3 py-1.5 text-xs font-medium capitalize ${view === v ? "bg-[#0A192F] text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
            >
              {v}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1 self-end">
          <button type="button" data-testid="cal-prev" onClick={goPrev} className="p-1.5 rounded-md border border-slate-200 hover:border-slate-400" aria-label="Previous">
            <ChevronLeft size={14} />
          </button>
          <button type="button" data-testid="cal-today" onClick={goToday} className="text-xs px-2.5 py-1.5 rounded-md border border-slate-200 hover:border-slate-400">
            Today
          </button>
          <button type="button" data-testid="cal-next" onClick={goNext} className="p-1.5 rounded-md border border-slate-200 hover:border-slate-400" aria-label="Next">
            <ChevronRight size={14} />
          </button>
        </div>

        <div className="text-sm font-semibold text-slate-900 self-end flex items-center gap-2 pb-1.5">
          {dateLabel(view, dateStr)}
          {viewIncludesToday && (
            <span className="text-[10px] font-semibold uppercase tracking-wide bg-[#0A192F] text-white rounded-full px-2 py-0.5">Today</span>
          )}
          {loading && <span className="text-xs text-slate-400 normal-case font-normal">Loading…</span>}
        </div>

        <div className="flex flex-wrap items-end gap-3 ml-auto">
          <div className="w-40">
            <Field
              label="Driver"
              testId="cal-filter-driver"
              value={driverId}
              onChange={(e) => updateParams({ driver_id: e.target.value })}
              options={[{ value: "", label: "All drivers" }, ...driverList.map((d) => ({ value: d.id, label: d.name }))]}
            />
          </div>
          {!isClient && (
            <div className="w-44">
              <Field
                label="Client company"
                testId="cal-filter-company"
                value={companyId}
                onChange={(e) => updateParams({ company_id: e.target.value })}
                options={[{ value: "", label: "All companies" }, ...companies.map((c) => ({ value: c.id, label: c.name }))]}
              />
            </div>
          )}
          <div className="w-44">
            <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Customer</label>
            <input
              type="text"
              list="cal-customer-options"
              data-testid="cal-filter-customer"
              value={customerText}
              onChange={onCustomerTextChange}
              placeholder="All customers"
              className="mt-1 w-full text-sm px-3 py-2.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300"
            />
            <datalist id="cal-customer-options">
              {customers.map((c) => <option key={c.id} value={`${c.first_name} ${c.last_name}`} />)}
            </datalist>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-1.5 flex-wrap">
          {STATUSES.map((s) => {
            const active = statuses.includes(s.value);
            return (
              <button
                key={s.value}
                type="button"
                onClick={() => toggleStatus(s.value)}
                data-testid={`cal-status-${s.value}`}
                className={`text-[11px] px-2.5 py-1 rounded-full border capitalize transition ${active ? (STATUS_CHIP[s.value] || STATUS_CHIP_FALLBACK) : "bg-white text-slate-400 border-slate-200"}`}
              >
                {s.label}
              </button>
            );
          })}
          <button type="button" onClick={clearFilters} className="text-xs text-slate-500 hover:text-slate-800 hover:underline ml-1" data-testid="cal-clear-filters">
            Clear filters
          </button>
        </div>
        <div className="text-xs text-slate-500" data-testid="cal-summary">
          {summary.total} pickup{summary.total === 1 ? "" : "s"} in view, {summary.pending} pending, {summary.assigned} assigned, {summary.completed} completed
        </div>
      </div>

      {view === "month" && (
        <div className="grid grid-cols-7 border-t border-l border-slate-200 bg-white rounded-md overflow-hidden" data-testid="cal-grid">
          {WEEKDAY_LABELS.map((w) => (
            <div key={w} className="border-b border-r border-slate-200 bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500 px-2 py-1.5 text-center">
              {w}
            </div>
          ))}
          {monthDays.map((day) => {
            const dayEvents = eventsByDate[day.date] || [];
            const visible = dayEvents.slice(0, 4);
            const extra = dayEvents.length - visible.length;
            const isToday = day.date === todayYmd();
            return (
              <div
                key={day.date}
                className={`border-b border-r border-slate-200 min-h-[104px] p-1.5 flex flex-col gap-1 ${day.inMonth ? "bg-white" : "bg-slate-50/60"} ${isToday ? "ring-1 ring-inset ring-[#0A192F]/40" : ""}`}
              >
                <div className="flex justify-end">
                  <div className={`text-[11px] font-medium w-5 h-5 flex items-center justify-center rounded-full ${isToday ? "bg-[#0A192F] text-white" : day.inMonth ? "text-slate-700" : "text-slate-400"}`}>
                    {Number(day.date.slice(8, 10))}
                  </div>
                </div>
                <div className="flex flex-col gap-0.5">
                  {visible.map((ev) => <EventChip key={ev.id} ev={ev} onClick={() => setSelected(ev)} />)}
                  {extra > 0 && (
                    <button type="button" className="text-[10px] text-slate-500 hover:text-slate-800 text-left px-1" onClick={() => goToDay(day.date)}>
                      +{extra} more
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {view === "week" && (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="cal-grid">
          <div className="grid grid-cols-[56px_repeat(7,1fr)] border-b border-slate-200">
            <div />
            {weekDays.map((d) => {
              const isToday = d === todayYmd();
              return (
                <div key={d} className={`px-2 py-2 text-center border-l border-slate-200 ${isToday ? "bg-slate-50" : ""}`}>
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">{weekdayShort(d)}</div>
                  <div className={`text-sm font-semibold ${isToday ? "text-[#0A192F]" : "text-slate-700"}`}>{Number(d.slice(8, 10))}</div>
                </div>
              );
            })}
          </div>
          <div className="overflow-y-auto" style={{ maxHeight: 560 }}>
            <div className="grid grid-cols-[56px_repeat(7,1fr)]">
              <div className="relative">
                {weekHours.map((h) => (
                  <div key={h} style={{ height: ROW_H }} className="text-[10px] text-slate-400 text-right pr-1.5 border-b border-slate-100">
                    {fmtHour(h)}
                  </div>
                ))}
              </div>
              {weekDays.map((d) => (
                <div key={d} className="relative border-l border-slate-200" style={{ height: ROW_H * weekHours.length }}>
                  {weekHours.map((h) => <div key={h} style={{ height: ROW_H }} className="border-b border-slate-100" />)}
                  {(eventsByDate[d] || []).map((ev) => (
                    <button
                      key={ev.id}
                      type="button"
                      data-testid={`cal-event-${ev.id}`}
                      onClick={() => setSelected(ev)}
                      style={{ top: eventPosition(ev), height: ROW_H - 6 }}
                      className={`absolute left-0.5 right-0.5 text-left rounded border px-1.5 py-0.5 text-[10px] overflow-hidden hover:opacity-80 ${STATUS_CHIP[ev.status] || STATUS_CHIP_FALLBACK}`}
                    >
                      <div className="flex items-center gap-1 font-medium">
                        <span className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${driverColor(ev.driver_id)}`} />
                        {fmtTime(ev.pickup_datetime)}
                      </div>
                      <div className="truncate">{ev.patient_name}{ev.driver_name ? ` · ${ev.driver_name}` : ""}</div>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {view === "day" && (
        <div className="space-y-4" data-testid="cal-grid">
          {dayGroups.length === 0 && <div className="empty">No pickups scheduled.</div>}
          {dayGroups.map(({ hour, items }) => (
            <div key={hour} className="flex gap-4">
              <div className="w-16 shrink-0 text-xs font-semibold text-slate-500 pt-3">{fmtHour(hour)}</div>
              <div className="flex-1 space-y-2">
                {items.map((ev) => (
                  <button
                    key={ev.id}
                    type="button"
                    data-testid={`cal-event-${ev.id}`}
                    onClick={() => setSelected(ev)}
                    className="w-full text-left bg-white border border-slate-200 rounded-md px-4 py-3 hover:border-slate-400 transition"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 font-mono text-xs text-slate-500">
                        <span className={`inline-block w-2 h-2 rounded-full ${driverColor(ev.driver_id)}`} />
                        {fmtTime(ev.pickup_datetime)}
                      </div>
                      <StatusBadge status={ev.status} />
                    </div>
                    <div className="mt-1.5 font-medium text-sm text-slate-900">{ev.patient_name}</div>
                    <div className="mt-1 text-xs text-slate-500">{ev.pickup_address} &rarr; {ev.dropoff_address}</div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                      <span>{ev.driver_name || "Unassigned"}{ev.vehicle_plate ? ` · ${ev.vehicle_plate}` : ""}</span>
                      {ev.company_name && <span>{ev.company_name}</span>}
                      <span className="capitalize">{ev.mobility}</span>
                      {ev.broker_po && (
                        <span className="font-mono text-[10px] uppercase bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">
                          {(ev.broker || "").toUpperCase()} {ev.broker_po}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Drawer open={!!selected} onClose={() => setSelected(null)} title={selected ? selected.patient_name : "Ride details"}>
        {selected && (
          <div data-testid="cal-drawer" className="space-y-4">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500 mb-1">Status</div>
              <StatusBadge status={selected.status} />
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500 mb-1">Pickup</div>
              <div className="text-sm text-slate-700">{fmtFullDateTime(selected.pickup_datetime)}</div>
              <div className="text-xs text-slate-500 mt-0.5">{selected.pickup_address}</div>
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500 mb-1">Dropoff</div>
              <div className="text-xs text-slate-500">{selected.dropoff_address}</div>
            </div>
            {selected.appointment_time && (
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500 mb-1">Appointment</div>
                <div className="text-sm text-slate-700">{selected.appointment_time}</div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500 mb-1">Mobility</div>
                <div className="text-sm text-slate-700 capitalize">{selected.mobility}</div>
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500 mb-1">Trip type</div>
                <div className="text-sm text-slate-700 capitalize">{(selected.trip_type || "").replace("_", " ")}</div>
              </div>
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500 mb-1">Driver / Vehicle</div>
              <div className="text-sm text-slate-700">
                {selected.driver_name || "Unassigned"}{selected.vehicle_plate ? ` · ${selected.vehicle_plate}` : ""}
              </div>
            </div>
            {selected.company_name && (
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500 mb-1">Client company</div>
                <div className="text-sm text-slate-700">{selected.company_name}</div>
              </div>
            )}
            {selected.broker_po && (
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500 mb-1">Broker</div>
                <span className="inline-block font-mono text-[10px] uppercase bg-slate-100 text-slate-600 border border-slate-200 rounded px-1.5 py-0.5">
                  {(selected.broker || "").toUpperCase()} {selected.broker_po}{selected.broker_direction ? ` · ${selected.broker_direction}` : ""}
                </span>
              </div>
            )}
            <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-200">
              {selected.driver_id && (
                <button
                  type="button"
                  className="text-xs px-2.5 py-1.5 rounded-md border border-slate-200 hover:border-slate-400"
                  onClick={() => filterByDriver(selected.driver_id)}
                >
                  Filter calendar by this driver
                </button>
              )}
              {selected.customer_id && (
                <button
                  type="button"
                  className="text-xs px-2.5 py-1.5 rounded-md border border-slate-200 hover:border-slate-400"
                  onClick={() => filterByCustomer(selected.customer_id, selected.patient_name)}
                >
                  Filter calendar by this customer
                </button>
              )}
            </div>
            <Link to="/app/rides" className="inline-block text-xs text-[#005E82] hover:underline">
              Open in Rides &rarr;
            </Link>
          </div>
        )}
      </Drawer>
    </div>
  );
}
